import { Link } from 'react-router-dom';
import { ArrowRight, Cloud as CloudIcon, Shield, RefreshCcw, BarChart3, GitBranch, Gauge, FileCheck2 } from 'lucide-react';
import AZURE_IMG from '../assets/images/Dgitaal_azure.png';

const cloudServices = [
  {
    title: 'Azure Migration',
    desc: 'Migreer uw on-premise infrastructuur naar Microsoft Azure met minimale downtime.',
  },
  {
    title: 'Azure Infrastructure',
    desc: 'Virtual machines, storage en networking - de volledige Azure infrastructuur stack.',
  },
  {
    title: 'Cloud Security',
    desc: 'Beveilig uw cloud omgeving met Azure Security Center en best practices.',
  },
  {
    title: 'Disaster Recovery',
    desc: 'Azure Site Recovery voor bedrijfscontinuiteit en disaster recovery.',
  },
  {
    title: 'Cost Optimization',
    desc: 'Optimaliseer uw Azure kosten zonder in te boeten op prestaties.',
  },
  {
    title: 'Hybrid Cloud',
    desc: 'Combineer on-premise en cloud resources voor maximale flexibiliteit.',
  },
];

const migrationJourney = [
  { nr: '1', title: 'Assess', desc: 'Analyse van uw huidige infrastructuur.' },
  { nr: '2', title: 'Plan', desc: 'Gedetailleerd migratieplan opstellen.' },
  { nr: '3', title: 'Migreer', desc: 'Gefaseerde migratie uitvoeren.' },
  { nr: '4', title: 'Optimaliseer', desc: 'Continue optimalisatie.' },
];

const azureBenefits = [
  { icon: Gauge, title: 'Schaalbaarheid', desc: 'Schaal resources op of af naar behoefte.' },
  { icon: BarChart3, title: 'Kostenreductie', desc: 'Bespaar op hardware investeringen.' },
  { icon: Shield, title: 'Beveiliging', desc: 'Enterprise-grade security infrastructuur.' },
  { icon: FileCheck2, title: 'Compliance', desc: 'Voldoe aan GDPR en ISO 27001.' },
  { icon: RefreshCcw, title: 'Innovatie', desc: 'Toegang tot AI en machine learning.' },
  { icon: GitBranch, title: 'Beschikbaarheid', desc: '99.99% uptime SLA.' },
];

export default function Cloud() {
  return (
    <div data-testid="cloud-page">
      <section className="relative pt-40 pb-24 overflow-hidden" style={{ background: 'linear-gradient(135deg, #16224b 0%, #233a80 58%, #f2a100 100%)' }}>
        <div className="dot-pattern absolute inset-0" />
        <div className="relative z-10 max-w-[1400px] mx-auto px-6 lg:px-10 text-center">
          <h1 className="text-white font-bold tracking-tight mb-4" style={{ fontSize: 'clamp(2rem, 4vw, 3rem)', letterSpacing: '-0.03em' }}>
            Cloud Solutions met Microsoft Azure
          </h1>
          <p className="text-white/70 max-w-xl mx-auto text-base leading-8">
            Transformeer uw IT-infrastructuur met de kracht van Azure. Schaalbaar, veilig en
            kostenefficient - wij begeleiden u bij elke stap.
          </p>
          <div className="mt-8 flex justify-center gap-4 flex-wrap">
            <Link to="/contact" className="btn-gradient">
              Start Cloud Journey <ArrowRight className="w-4 h-4" />
            </Link>
            <Link to="/contact" className="btn-outline-white">
              Gratis Assessment
            </Link>
          </div>
        </div>
      </section>

      <section className="py-24 bg-white">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="text-center mb-12">
            <h2 className="text-[#16224b] font-bold" style={{ fontSize: 'clamp(1.75rem, 3vw, 2.4rem)' }}>
              Complete Cloud Oplossingen
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-7">
            {cloudServices.map((service, i) => (
              <div key={i} className="admin-surface p-7 hover:-translate-y-1 transition-all duration-300">
                <h3 className="text-[#16224b] text-lg font-bold mb-3">{service.title}</h3>
                <p className="text-[#6b7184] text-sm leading-7">{service.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 bg-[#f8f9fd]">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="text-center mb-12">
            <h2 className="text-[#16224b] font-bold" style={{ fontSize: 'clamp(1.75rem, 3vw, 2.4rem)' }}>
              Uw pad naar de cloud
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {migrationJourney.map((step) => (
              <div key={step.nr} className="bg-white border border-gray-100 rounded-lg p-6">
                <p className="text-[#f2a100] text-xs font-bold tracking-widest mb-2">{step.nr}</p>
                <h3 className="text-[#16224b] text-lg font-bold mb-2">{step.title}</h3>
                <p className="text-[#6b7184] text-sm leading-7">{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 bg-white">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <h2 className="text-[#16224b] font-bold mb-5" style={{ fontSize: 'clamp(1.75rem, 3vw, 2.4rem)' }}>
              Waarom kiezen voor Azure?
            </h2>
            <p className="text-[#6b7184] text-base leading-8 mb-8">
              Microsoft Azure is het meest vertrouwde cloud platform voor bedrijven wereldwijd.
            </p>

            <div className="grid sm:grid-cols-2 gap-4">
              {azureBenefits.map((benefit) => (
                <div key={benefit.title} className="bg-[#f8f9fd] rounded-lg p-4 border border-gray-100">
                  <benefit.icon className="w-5 h-5 text-[#3d56a6] mb-2" />
                  <h4 className="text-[#16224b] text-sm font-bold mb-1">{benefit.title}</h4>
                  <p className="text-[#6b7184] text-xs leading-6">{benefit.desc}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="rounded-lg overflow-hidden shadow-2xl border border-gray-100">
            <img
              src={AZURE_IMG}
              alt="Azure datacenter"
              className="w-full h-[520px] object-cover"
            />
          </div>
        </div>
      </section>

      <section className="py-20 bg-[#f8f9fd] text-center">
        <div className="max-w-2xl mx-auto px-6">
          <h2 className="text-[#16224b] font-bold mb-4" style={{ fontSize: 'clamp(1.5rem, 3vw, 2rem)' }}>
            Klaar voor de cloud?
          </h2>
          <p className="text-[#6b7184] mb-8">
            Plan een gratis cloud assessment en ontdek hoe Azure uw bedrijf kan transformeren.
          </p>
          <Link to="/contact" className="btn-gradient text-base px-10 py-4">
            Plan Cloud Assessment <ArrowRight className="w-5 h-5" />
          </Link>
        </div>
      </section>
    </div>
  );
}
